import { animate, style, transition, trigger } from '@angular/animations'
import { NgClass } from '@angular/common'
import { Component, computed, DestroyRef, effect, inject, OnInit, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { InvoicePlansComponent } from '@app/modules/auth/components/invoice-plans/invoice-plans.component'
import { BillingService } from '@app/modules/payments/services/billing.service'
import { BetaFeaturesModalComponent } from '@app/modules/shared/components/beta-features-modal/beta-features-modal.component'
import { OrganizationFormData, ParentOrganization, Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '@app/modules/workspaces/services/workspace.service'
import { OrganizationSelectionModalComponent } from '@app/v2/organization-switcher/components/org-modal/organization-selection-modal.component'
import { getOrganizationSettingsUrl } from '@app/v2/settings/helpers/organization-settings-url.helper'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { AuthService } from '@auth/services/auth.service'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { filter, interval, take, timer } from 'rxjs'
import { AddNewOrganizationModalComponent } from '../../../organizations/add-new-organization-modal/add-new-organization-modal.component'
import { Alert } from '../../enums/alert.enum'
import { AlertService } from '../../services/alert.service'
import { ModalService } from '../../services/modal.service'
import { QueuedJobsService } from '../../services/queued-jobs.service'
import { ModalComponent } from '../modal/modal.component'
import { WizardService } from '../wizard/services/wizard.service'
import { GoToBillingCardComponent } from './go-to-billing-card/go-to-billing-card.component'
import { MobileDrawerComponent } from './mobile-drawer/mobile-drawer.component'
import { MobileDrawerService } from './mobile-drawer/mobile-drawer.service'
import { NavbarService } from './navbar.service'
import { QueuedJobsMenuComponent } from './queued-jobs/queued-jobs-menu/queued-jobs-menu.component'

enum UserOptions {
  PROFILE,
  ADMIN_TOOLS,
  BETA_FEATURES,
  LOGOUT
}
export enum SystemOptions {
  PLANNING,
  CREW
}
export enum WorkspaceOptions {
  SETTINGS,
  SWITCH_WORKSPACE,
  ADD_NEW_WORKSPACE
}

/**
 * Represents shared module header
 */
@Component({
  selector: 'app-navbar',
  styleUrls: ['./navbar.component.scss'],
  templateUrl: './navbar.component.html',
  imports: [
    DropdownComponent,
    TranslateModule,
    ModalComponent,
    InvoicePlansComponent,
    GoToBillingCardComponent,
    MobileDrawerComponent,
    NgClass,
    OrganizationSelectionModalComponent,
    AddNewOrganizationModalComponent,
    BetaFeaturesModalComponent,
    QueuedJobsMenuComponent
  ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms ease-in', style({ opacity: 1 }))]),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))])
    ])
  ]
})
export class NavbarComponent implements OnInit {
  public dropdownHeightInPX = signal<number>(96)
  /** Subject that stops all hot observables */
  public userDropdownOptions: DropdownOption[] = []
  public systemDropdownOptions = signal<DropdownOption[]>([
    {
      key: SystemOptions.CREW,
      value: `<svg xmlns="http://www.w3.org/2000/svg" width="74" height="26" viewBox="0 0 74 26" fill="none" class="tw-cursor-pointer">
      <path
        d="M54.8311 0.0324853C55.1792 -0.0330703 55.5385 0.0371163 55.8392 0.229397C56.1399 0.421678 56.3606 0.722315 56.4581 1.07287C56.533 1.33068 56.5386 1.60457 56.4746 1.86546C56.4105 2.12634 56.2791 2.36447 56.0943 2.55457C55.9094 2.74467 55.6781 2.87963 55.4248 2.94513C55.1715 3.01063 54.9057 3.00423 54.6557 2.92659C53.244 2.55922 53.4308 0.252905 54.8311 0.0324853Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M58.4907 0.0133657C59.2584 -0.000594201 60.0268 -0.00794151 60.7959 0.0133657C60.5728 1.9075 60.356 3.80312 60.108 5.69431C60.6133 5.03431 61.2644 4.50832 62.0069 4.16019C62.4681 3.96595 62.9751 3.91849 63.4627 4.0239C63.9502 4.12932 64.396 4.38278 64.7426 4.75165C65.5431 5.71709 65.4967 7.1072 65.4476 8.296C65.2734 9.96161 64.5061 11.5014 63.2942 12.6177C62.7166 13.1774 61.9493 13.4816 61.1567 13.4653C60.3641 13.449 59.6092 13.1133 59.0538 12.5302C58.9611 12.8366 58.8486 13.1915 58.5207 13.3039C57.9661 13.3333 57.4109 13.3429 56.8577 13.3186C57.3688 9.06746 57.8492 4.8119 58.3603 0.560008C58.4316 0.40498 58.3311 0.139003 58.4907 0.016303M61.8173 5.92795C60.0068 6.90221 59.5036 9.24086 59.4836 11.1805C59.7385 11.4084 60.0496 11.5593 60.3824 11.6163C60.7153 11.6734 61.0569 11.6344 61.3696 11.5038C61.7106 11.3133 62.0112 11.0545 62.2538 10.7426C62.4964 10.4307 62.6761 10.072 62.7824 9.68758C63.1618 8.71947 63.232 7.65191 62.9827 6.6399C62.9536 6.51313 62.9 6.39371 62.8252 6.28875C62.7503 6.18379 62.6558 6.09545 62.5471 6.029C62.4384 5.96255 62.3178 5.91936 62.1925 5.902C62.0673 5.88464 61.9392 5.89347 61.8173 5.92795Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M26.7289 0.260582C27.5421 0.123011 28.3762 0.215926 29.1421 0.52938C29.908 0.842834 30.5768 1.36503 31.0769 2.0401C30.6393 2.49931 30.2216 3.62125 29.451 3.35087C28.801 2.73709 27.9487 2.40123 27.0673 2.41147C26.1858 2.4217 25.3411 2.77726 24.7046 3.40597C23.3881 4.61828 23.0809 6.56531 23.1728 8.29927C23.2575 9.23111 23.6619 10.1012 24.3132 10.7533C24.8262 11.1141 25.4298 11.3137 26.0507 11.3278C26.6715 11.3418 27.283 11.1699 27.8109 10.8326C28.1473 10.6306 28.4125 10.264 28.8088 10.1912C29.3177 10.4425 29.5608 11.0303 29.9493 11.4292C29.1953 12.3389 28.1914 12.9915 27.0701 13.3008C25.9488 13.6101 24.7629 13.5616 23.6689 13.1617C22.9268 12.859 22.2712 12.3672 21.7634 11.7325C21.2556 11.0978 20.9121 10.3408 20.765 9.53216C20.1185 6.48082 21.2055 2.90929 23.8663 1.22969C24.7336 0.679806 25.7142 0.347835 26.7289 0.260582Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M1.80158 0.376893C2.58916 0.312023 3.38137 0.341606 4.16232 0.465057C4.56719 0.790543 4.58574 1.39376 4.80385 1.83974C5.57651 4.19088 6.43256 6.51704 7.15604 8.88287C8.72917 6.13498 10.3051 3.31949 11.9645 0.600252C12.7436 0.101369 13.77 0.482685 14.6339 0.373944C14.1235 4.68878 13.6025 9.00214 13.0708 13.314C12.3451 13.3302 11.6195 13.328 10.8939 13.314C11.2032 10.3229 11.5475 7.32891 11.946 4.35031C10.9032 6.12101 9.89814 7.91376 8.84963 9.68079C8.50496 10.4078 8.0693 11.0851 7.55379 11.6954C6.975 11.8571 5.99634 11.9952 5.82313 11.1987C5.00485 8.90344 4.25928 6.58096 3.42389 4.29227C3.16087 7.30467 2.78665 10.3127 2.40245 13.3111C1.67897 13.3287 0.955505 13.3258 0.232025 13.3155C0.641878 9.1775 1.20355 5.05125 1.67257 0.918389C1.75525 0.7663 1.63621 0.501062 1.80158 0.376893Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M17.1093 0.377126C17.9268 0.356554 18.7487 0.363163 19.5655 0.37198C19.1692 4.69293 18.5605 9.00067 18.0687 13.3143C17.2255 13.3275 16.383 13.326 15.5404 13.3143C16.0736 9.00655 16.4956 4.66575 17.1093 0.377126Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M32.8509 0.375275C35.3107 0.351029 37.7691 0.375281 40.2311 0.363525C40.1699 1.0448 40.0747 1.72238 39.946 2.39359C38.3308 2.42812 36.7142 2.37815 35.099 2.41195C34.9728 3.55079 34.841 4.68962 34.6913 5.82625C35.9537 5.83286 37.2174 5.80641 38.4805 5.82625C38.4246 6.46716 38.3373 7.10475 38.2189 7.73656C37.0072 7.86881 35.7134 7.73655 34.4711 7.79606C34.3007 8.95253 34.208 10.1215 34.0291 11.2758C35.6543 11.2963 37.2801 11.2611 38.9046 11.2875C38.8716 11.966 38.7969 12.6416 38.6808 13.3102C36.2145 13.3249 33.749 13.319 31.2835 13.3102C31.776 9.00029 32.2436 4.66611 32.8509 0.375275Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M42.5996 1.39965C43.1085 1.37467 43.6203 1.371 44.1321 1.38863C44.0415 2.31145 43.9182 3.23281 43.802 4.15342C44.4841 4.11711 45.1679 4.13284 45.8477 4.20046C45.8542 4.76936 45.7926 5.33691 45.6645 5.89033C44.9689 5.91531 44.2746 5.88445 43.5804 5.90503C43.4678 7.62356 43.1185 9.32888 43.0237 11.0437C43.137 11.7616 43.9781 11.4714 44.4115 11.2833C44.7113 11.6998 44.9438 12.1635 45.1 12.6565C43.9653 13.641 41.7977 13.9555 40.9117 12.4956C40.4448 11.5632 40.7328 10.4626 40.8197 9.46701C40.9573 8.27968 41.1084 7.09382 41.2381 5.9065C40.9074 5.81465 40.2916 6.09973 40.1276 5.65595C40.0911 5.27128 40.1301 4.88296 40.2424 4.51418C40.628 4.11595 41.6344 4.53843 41.705 3.77137C42.0507 3.00431 42.0108 2.01903 42.5996 1.39965Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M70.4933 4.04279C71.3365 3.88485 72.2066 4.0525 72.9375 4.51375C73.3923 4.78539 73.7337 5.22071 73.896 5.73607C74.0584 6.25143 74.0303 6.81045 73.817 7.30572C73.5155 8.18739 72.6153 8.5981 71.8291 8.90449C70.7827 9.20842 69.7143 9.42586 68.6344 9.55473C68.5654 9.95484 68.6317 10.3672 68.8221 10.723C69.0126 11.0788 69.3156 11.3566 69.6807 11.5099C70.1534 11.6167 70.6439 11.6057 71.1116 11.478C71.5794 11.3502 72.0108 11.1092 72.3701 10.7751C73.0337 10.3901 73.4065 11.1587 73.75 11.5943C72.3344 13.5509 68.9445 14.3128 67.196 12.3217C65.0505 9.47391 66.9722 4.48803 70.4926 4.04131M70.7064 5.71136C69.6736 5.89211 69.0812 6.96774 68.8403 7.93538C69.5057 7.88695 70.1629 7.75445 70.797 7.54084C71.2453 7.37552 71.8006 7.13012 71.812 6.55262C71.9574 5.94059 71.2068 5.53061 70.7043 5.71356L70.7064 5.71136Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M47.403 4.09027C47.9882 4.14685 48.8065 3.88969 49.2734 4.39371C49.3764 4.96667 49.4099 5.5506 49.3732 6.13209C49.9562 4.98297 51.1452 3.61122 52.5365 4.24382C52.4061 5.00794 52.2685 5.77574 52.1089 6.53472C51.7127 6.33544 51.257 6.30193 50.8374 6.44122C50.4178 6.58051 50.0667 6.8818 49.8578 7.28194C48.7886 9.08424 49.0075 11.3075 48.6461 13.317C47.8727 13.3251 47.1008 13.3376 46.3296 13.317C46.4008 11.7138 46.6916 10.1261 46.8392 8.52803C47.0395 7.05269 47.1264 5.54872 47.403 4.09027Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M53.6863 4.16254C54.4556 4.15005 55.2263 4.14123 55.997 4.16254C55.6719 7.21682 55.3269 10.2733 54.9226 13.3165C54.1512 13.3283 53.3804 13.3393 52.6104 13.3165C52.8136 10.9147 53.1509 8.52464 53.404 6.12648C53.4501 5.46546 53.5445 4.80896 53.6863 4.16254Z"
        class="tw-fill-black dark:tw-fill-white"
      />
      <path
        d="M8.04594 20.6329H6.37164C6.3768 20.4065 6.34838 20.2064 6.28637 20.0326C6.22436 19.8562 6.13134 19.7061 6.00732 19.5824C5.88588 19.4586 5.73732 19.3651 5.56162 19.302C5.38592 19.2361 5.18826 19.2032 4.96864 19.2032C4.54489 19.2032 4.15603 19.3112 3.80205 19.5271C3.44807 19.743 3.14965 20.0563 2.90677 20.467C2.66389 20.8751 2.49595 21.3688 2.40293 21.948C2.3125 22.5062 2.31766 22.9735 2.41843 23.35C2.5192 23.7265 2.69748 24.0109 2.95328 24.2031C3.21166 24.3926 3.53334 24.4874 3.91832 24.4874C4.15603 24.4874 4.38211 24.4571 4.59657 24.3966C4.81103 24.3334 5.0061 24.2439 5.1818 24.128C5.36008 24.0095 5.51511 23.8661 5.64688 23.6975C5.78124 23.529 5.88588 23.3382 5.96081 23.1249H7.64674C7.5408 23.4961 7.37544 23.8542 7.15065 24.1991C6.92844 24.544 6.65327 24.8521 6.32513 25.1233C5.99699 25.3918 5.62363 25.6051 5.20505 25.763C4.78648 25.921 4.32786 26 3.82918 26C3.1083 26 2.48949 25.8315 1.97273 25.4945C1.45855 25.1575 1.08778 24.6717 0.860404 24.0372C0.63303 23.4027 0.590397 22.6378 0.732506 21.7426C0.874614 20.8764 1.15237 20.1418 1.56578 19.5389C1.98177 18.9334 2.4869 18.4739 3.08117 18.1606C3.67803 17.8473 4.31623 17.6906 4.99577 17.6906C5.4686 17.6906 5.89622 17.7565 6.27862 17.8881C6.66102 18.0197 6.98658 18.2119 7.25529 18.4647C7.52659 18.7148 7.73071 19.0216 7.86765 19.3849C8.00459 19.7482 8.06402 20.1642 8.04594 20.6329Z"
        fill="#FFB500"
      />
      <path
        d="M10.4742 25.8894L11.7919 17.8012H14.9235C15.5229 17.8012 16.0164 17.9092 16.404 18.1251C16.7942 18.341 17.0681 18.6477 17.2257 19.0453C17.3859 19.4402 17.4233 19.9062 17.3381 20.4433C17.2502 20.9804 17.0603 21.4412 16.7683 21.8256C16.479 22.21 16.1017 22.5062 15.6366 22.7142C15.1741 22.9195 14.638 23.0222 14.0282 23.0222H11.9315L12.164 21.6479H13.9856C14.306 21.6479 14.5799 21.6031 14.8072 21.5136C15.0346 21.4241 15.2142 21.2898 15.346 21.1107C15.4803 20.9317 15.5682 20.7092 15.6095 20.4433C15.6534 20.1721 15.6379 19.9457 15.563 19.764C15.4881 19.5797 15.3524 19.4402 15.156 19.3454C14.9597 19.248 14.7013 19.1993 14.3809 19.1993H13.2492L12.1524 25.8894H10.4742ZM15.3576 22.2087L16.7335 25.8894H14.8809L13.5476 22.2087H15.3576Z"
        fill="#FFB500"
      />
      <path
        d="M19.7741 25.8894L21.0918 17.8012H26.4403L26.2077 19.2111H22.5375L22.2274 21.1384H25.6225L25.39 22.5483H21.9949L21.6848 24.4795H25.3706L25.138 25.8894H19.7741Z"
        fill="#FFB500"
      />
      <path
        d="M30.3171 25.8894L29.3637 17.8012H31.1969L31.5999 23.4211H31.6658L34.0261 17.8012H35.5958L36.1306 23.4329H36.2004L38.425 17.8012H40.2582L36.6693 25.8894H35.0338L34.3904 20.6013H34.3284L31.9526 25.8894H30.3171Z"
        fill="#FFB500"
      />
    </svg>`
    }
  ])
  private _translationVersion = signal(0)
  private _workspaceRoleVersion = signal(0)
  public workspaceDropdownOptions = computed<DropdownOption[]>(() => {
    this._translationVersion()
    this._workspaceRoleVersion()
    return this._buildWorkspaceDropdownOptions()
  })
  public workspaceDropdownHeightInPX = computed<number>(() => this._menuDropdownHeightInPX(this.workspaceDropdownOptions().length))
  public showQueuedJobsIcon = computed<boolean>(() => {
    this._workspaceRoleVersion()
    return !this._workspaceRoleService.isAccountant()
  })
  public showWallet = signal(false)
  public showUpgradeButton = signal(false)
  public isModalOpen = signal<boolean>(false)
  public workspace = signal<Workspace | null>(null)
  public isOrganizationSwitcherOpen = signal<boolean>(false)
  public isDropdownOpen = signal<boolean>(false)
  public balance = signal<number | null>(null)
  public isBalanceLoaded = signal<boolean>(false)
  private isBalanceFetching = signal<boolean>(false)
  private _isInitializingWorkspaceState = false
  public parentOrganizations = signal<ParentOrganization[]>([])
  public isMobileView = signal<boolean>(false)
  private readonly _queuedJobsService = inject(QueuedJobsService)

  @ViewChild('betaFeaturesModal') betaFeaturesModal!: BetaFeaturesModalComponent

  /** Current authenticated user */
  get user() {
    return this._auth.user
  }
  get userAvatar() {
    return this._auth.avatar
  }
  get userInitials() {
    return this._auth.userInitials
  }
  get currentWorkspaceId() {
    return this._auth.currentWorkspace?.id
  }

  /**
   * Creates the component and injects it's dependencies
   * @param _auth AuthService
   * @param _router Router
   * @param _translate TranslateService
   * @param _mobileDrawerService MobileDrawerService
   * @param _navbarService NavbarService
   * @param _ModalService ModalService
   */
  constructor(
    private _auth: AuthService,
    private _router: Router,
    private _translate: TranslateService,
    private _mobileDrawerService: MobileDrawerService,
    private _ModalService: ModalService,
    private _navbarService: NavbarService,
    private _destroyRef: DestroyRef,
    private _alertService: AlertService,
    private _billingService: BillingService,
    public organizationService: OrganizationService,
    private _workspaceService: WorkspaceService,
    private _workspaceRoleService: WorkspaceRoleService,
    private _wizardService: WizardService
  ) {
    this._navbarService.BalanceChanged.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._fetchBalance()
    })

    effect(() => this._syncQueuedJobsSocket())

    toObservable(this._auth.workspaceList)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((workspaces) => {
        if (workspaces.length > 0 && !this._auth.currentWorkspace) {
          this._initializeWorkspaceState(workspaces)
        }
      })
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    this._initDropdowns()
    this._destroyRef.onDestroy(() => this._queuedJobsService.disconnectSocket())

    // Initialize mobile view detection
    this._initMobileViewDetection()

    // Initialize workspace state on component startup
    this._initializeWorkspaceState()
    this._handleWorkspacePlanExpiry()

    // Bridge non-signal services into the computed dropdown options.
    this._translate.onLangChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._translationVersion.update((version) => version + 1)
      this.setUserDropdownOptions()
    })

    this._workspaceRoleService
      .getUserRole()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._workspaceRoleVersion.update((version) => version + 1)
      })

    this._auth.currentParentWorkspaceChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((parentWorkspace) => {
      this.showUpgradeButton.set(parentWorkspace && parentWorkspace.plan_id !== 2 && !this._auth.isAdmin)
      this.showWallet.set(!!parentWorkspace)

      if (!parentWorkspace) {
        // When parent workspace is null, clear the balance and set loaded to true
        this.balance.set(null)
        this.isBalanceLoaded.set(true)
      }
    })

    this._auth.currentWorkspaceChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((workspace) => {
      const currentWorkspaceId = this.workspace()?.id
      // Clear the editing organization name when workspace changes
      this.organizationService.organizationNameWhileEditing.set(null)
      this.workspace.set(workspace)

      if (workspace?.id === currentWorkspaceId) return

      // Ensure parent workspace is set if it's not already set
      if (workspace && !this._auth.currentParentWorkspace) {
        this._auth.currentParentWorkspace = workspace
      }

      // Only fetch balance when we have both parent workspace and workspace
      if (workspace && this._auth.currentParentWorkspace) {
        this.isBalanceLoaded.set(false)
        this._fetchBalance()
      }
    })

    this._getBalance()
  }

  private _initDropdowns() {
    this.setUserDropdownOptions()
  }

  /**
   * Initialize workspace state on component startup
   * Ensures both currentWorkspace and currentParentWorkspace are properly set
   */
  private _initializeWorkspaceState(workspaces: Workspace[] = this._auth.workspaces) {
    const currentWorkspace = this._auth.currentWorkspace
    const currentParentWorkspace = this._auth.currentParentWorkspace

    // If we have a workspace but no parent workspace, set parent workspace to the same
    if (currentWorkspace && !currentParentWorkspace) {
      this._auth.currentParentWorkspace = currentWorkspace
    }

    if (currentWorkspace || this._isInitializingWorkspaceState) {
      return
    }

    // If we have workspaces but no current workspace, set the first one
    if (workspaces.length > 0) {
      this._isInitializingWorkspaceState = true
      this._auth
        .getLastUsedWorkspace()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((lastUsedWorkspaceId) => {
          let workspaceTobeSet: Workspace | null = null

          if (lastUsedWorkspaceId) {
            // Find the workspace in the existing workspaces list
            workspaceTobeSet = workspaces.find((w) => w.id === lastUsedWorkspaceId) || null
          }

          // If no last-used workspace found, use the first available workspace
          if (!workspaceTobeSet) {
            workspaceTobeSet = this._auth.currentWorkspace ?? (workspaces.length > 0 ? workspaces[0] : null)
          }

          this.workspace.set(workspaceTobeSet)
          this._auth.currentWorkspace = workspaceTobeSet
          this._fetchBalance()
          this._isInitializingWorkspaceState = false

          // Set parent workspace to the same workspace to show wallet/upgrade buttons
          if (workspaceTobeSet && this._auth.currentParentWorkspace !== workspaceTobeSet) {
            this._auth.currentParentWorkspace = workspaceTobeSet
          }
        })
    }
  }

  /**
   * Handles the workspace plan expiry
   * If the workspace plan is expired, it will set the plan to the basic plan
   * and update the current parent workspace
   */
  private _handleWorkspacePlanExpiry() {
    // ~ 24 days in milliseconds
    const MAX_TIMEOUT_DELAY = 2147483647
    const currentParentWorkspace = this._auth.currentParentWorkspace
    if (!currentParentWorkspace?.plan_expiry_date) return

    const expiryTime = new Date(currentParentWorkspace.plan_expiry_date).getTime()
    const delay = Math.min(expiryTime - Date.now(), MAX_TIMEOUT_DELAY)

    // If plan is already expired, update immediately
    if (delay <= 0) {
      this._setWorkspaceToBasicPlan(currentParentWorkspace)
      return
    }

    // Set timer to update when plan expires
    timer(delay)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._setWorkspaceToBasicPlan(currentParentWorkspace)
      })
  }

  /**
   * Sets the workspace plan to Basic (plan_id = 1)
   * and updates the current parent workspace
   */
  private _setWorkspaceToBasicPlan(workspace: Workspace) {
    workspace.plan_id = 1
    workspace.plan = this._auth.plans.find((plan) => plan.id === 1)
    this._auth.currentParentWorkspace = workspace
  }
  /**
   * Get the balance for the current workspace
   */
  private _getBalance() {
    // Keep navbar balance in sync with billing UI (top-up / billing refetches).
    this._billingService.walletBalanceUpdated$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((balance) => {
      if (!this.workspace()?.id) return

      this.balance.set(balance)
      this.isBalanceLoaded.set(true)
    })

    // Listen for manual balance refresh requests from other components
    this._navbarService.triggerBalanceRefresh.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.workspace()?.id) {
        this._fetchBalance()
      }
    })

    // Set up interval to call getBalance every 30 minutes.
    interval(1800000)
      .pipe(
        filter(() => !!this.workspace()), // Only call if we have a workspace
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._fetchBalance()
      })
  }

  /**
   * Fetch the navbar wallet balance from the get-balance API.
   */
  private _fetchBalance() {
    if (!this.workspace()) {
      return
    }

    // Prevent duplicate calls if already fetching
    if (this.isBalanceFetching()) {
      return
    }
    this.isBalanceFetching.set(true)

    this._navbarService
      .getBalance(this.workspace().id.toString())
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe({
        next: (balance) => {
          this.balance.set(balance)
          this.isBalanceLoaded.set(true)
          this.isBalanceFetching.set(false)
        },
        error: (error) => {
          this.balance.set(0)
          this.isBalanceLoaded.set(true)
          this.isBalanceFetching.set(false)
          // Expected when the user cannot view this workspace's billing (e.g. sub-org without parent billing access).
          if (error.status !== 403) {
            this._alertService.alert(Alert.ERROR, error.error.message)
          }
          console.error('Error fetching balance:', error)
        }
      })
  }

  /**
   * Same height rules as the user profile menu so the panel fits its options.
   */
  private _menuDropdownHeightInPX(optionCount: number): number {
    return optionCount <= 7 ? Math.max(optionCount, 1) * 49 : 96
  }

  public onWorkspaceDropdownOpened(): void {
    this.isDropdownOpen.set(true)
  }

  private _buildWorkspaceDropdownOptions(): DropdownOption[] {
    const workspaces = this._auth.workspaceList()

    if (workspaces.length === 0) {
      return []
    }

    const organizationSettingsText = this._translate.instant('v2.header.ORGANIZATION_SETTINGS')
    const switchOrganizationText = this._translate.instant('v2.header.SWITCH_ORGANIZATION')
    const addNewOrganizationText = this._translate.instant('v2.header.ADD_NEW_ORGANIZATION')
    const options: DropdownOption[] = []

    // Only add settings option if user can access workspace settings
    if (this._workspaceRoleService.canAccessWorkspaceSettings()) {
      options.push({
        key: WorkspaceOptions.SETTINGS,
        value: `
      <span class="tw-flex-1 tw-justify-start tw-text-gray-600 dark:tw-text-gray-100 tw-text-sm tw-font-normal tw-font-inter tw-leading-none">${organizationSettingsText}</span>
        `,
        prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <g clip-path="url(#clip0_5977_8895)">
          <path d="M13.2222 5.44444H12.6334L12.0836 4.11678L12.4997 3.69989C12.6455 3.55403 12.7274 3.35624 12.7274 3.15C12.7274 2.94376 12.6455 2.74597 12.4997 2.60011L11.3999 1.50033C11.254 1.35452 11.0562 1.27261 10.85 1.27261C10.6438 1.27261 10.446 1.35452 10.3001 1.50033L9.88322 1.91644L8.55556 1.36656V0.777778C8.55556 0.571498 8.47361 0.373667 8.32775 0.227806C8.18189 0.0819442 7.98406 0 7.77778 0H6.22222C6.01594 0 5.81811 0.0819442 5.67225 0.227806C5.52639 0.373667 5.44444 0.571498 5.44444 0.777778V1.36656L4.11678 1.91644L3.69989 1.50033C3.55403 1.35452 3.35624 1.27261 3.15 1.27261C2.94376 1.27261 2.74597 1.35452 2.60011 1.50033L1.50033 2.60011C1.35452 2.74597 1.27261 2.94376 1.27261 3.15C1.27261 3.35624 1.35452 3.55403 1.50033 3.69989L1.91722 4.11678L1.36656 5.44444H0.777778C0.571498 5.44444 0.373667 5.52639 0.227806 5.67225C0.0819442 5.81811 0 6.01594 0 6.22222V7.77778C0 7.98406 0.0819442 8.18189 0.227806 8.32775C0.373667 8.47361 0.571498 8.55556 0.777778 8.55556H1.36656C1.63178 9.19567 1.652 9.24311 1.91644 9.88322L1.50033 10.3001C1.35452 10.446 1.27261 10.6438 1.27261 10.85C1.27261 11.0562 1.35452 11.254 1.50033 11.3999L2.60011 12.4997C2.74597 12.6455 2.94376 12.7274 3.15 12.7274C3.35624 12.7274 3.55403 12.6455 3.69989 12.4997L4.11678 12.0836L5.44444 12.6334V13.2222C5.44444 13.4285 5.52639 13.6263 5.67225 13.7722C5.81811 13.9181 6.01594 14 6.22222 14H7.77778C7.98406 14 8.18189 13.9181 8.32775 13.7722C8.47361 13.6263 8.55556 13.4285 8.55556 13.2222V12.6334L9.88322 12.0828L10.3001 12.4997C10.446 12.6455 10.6438 12.7274 10.85 12.7274C11.0562 12.7274 11.254 12.6455 11.3999 12.4997L12.4997 11.3999C12.6455 11.254 12.7274 11.0562 12.7274 10.85C12.7274 10.6438 12.6455 10.446 12.4997 10.3001L12.0836 9.88322L12.6334 8.55556H13.2222C13.4285 8.55556 13.6263 8.47361 13.7722 8.32775C13.9181 8.18189 14 7.98406 14 7.77778V6.22222C14 6.01594 13.9181 5.81811 13.7722 5.67225C13.6263 5.52639 13.4285 5.44444 13.2222 5.44444ZM7 10.1111C6.38468 10.1111 5.78318 9.92865 5.27156 9.58679C4.75994 9.24494 4.36118 8.75905 4.12571 8.19057C3.89024 7.62209 3.82862 6.99655 3.94867 6.39305C4.06871 5.78956 4.36502 5.23521 4.80011 4.80011C5.23521 4.36502 5.78956 4.06871 6.39305 3.94867C6.99655 3.82862 7.62209 3.89024 8.19057 4.12571C8.75905 4.36118 9.24494 4.75994 9.58679 5.27156C9.92865 5.78318 10.1111 6.38468 10.1111 7C10.1111 7.82512 9.78333 8.61644 9.19989 9.19989C8.61644 9.78333 7.82512 10.1111 7 10.1111Z" 
          class="tw-fill-blue-600 dark:tw-fill-blue-500" />
          </g>
          <defs>
          <clipPath id="clip0_5977_8895">
          <rect width="14" height="14" fill="white"/>
          </clipPath>
          </defs>
        </svg>`
      })
    }
    if (!this._auth.user?.is_admin) {
      options.splice(1, 0, {
        key: WorkspaceOptions.ADD_NEW_WORKSPACE,
        value: `
        <span class="tw-flex-1 tw-justify-start tw-text-gray-600 dark:tw-text-gray-100 tw-text-sm tw-font-normal tw-font-inter tw-leading-none">${addNewOrganizationText}</span>
          `,
        prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <g clip-path="url(#clip0_5977_14972)">
              <path d="M13.2222 6.22222H7.77778V0.777778C7.77778 0.571498 7.69583 0.373667 7.54997 0.227806C7.40411 0.0819442 7.20628 0 7 0C6.79372 0 6.59589 0.0819442 6.45003 0.227806C6.30417 0.373667 6.22222 0.571498 6.22222 0.777778V6.22222H0.777778C0.571498 6.22222 0.373667 6.30417 0.227806 6.45003C0.0819442 6.59589 0 6.79372 0 7C0 7.20628 0.0819442 7.40411 0.227806 7.54997C0.373667 7.69583 0.571498 7.77778 0.777778 7.77778H6.22222V13.2222C6.22222 13.4285 6.30417 13.6263 6.45003 13.7722C6.59589 13.9181 6.79372 14 7 14C7.20628 14 7.40411 13.9181 7.54997 13.7722C7.69583 13.6263 7.77778 13.4285 7.77778 13.2222V7.77778H13.2222C13.4285 7.77778 13.6263 7.69583 13.7722 7.54997C13.9181 7.40411 14 7.20628 14 7C14 6.79372 13.9181 6.59589 13.7722 6.45003C13.6263 6.30417 13.4285 6.22222 13.2222 6.22222Z"
              class="tw-fill-blue-600 dark:tw-fill-blue-500" />
              </g>
              <defs>
              <clipPath id="clip0_5977_14972">
              <rect width="14" height="14" fill="white"/>
              </clipPath>
              </defs>
              </svg>`
      })
    }

    if (workspaces.length > 1) {
      options.splice(1, 0, {
        key: WorkspaceOptions.SWITCH_WORKSPACE,
        value: `
        <span class="tw-flex-1 tw-justify-start tw-text-gray-600 dark:tw-text-gray-100 tw-text-sm tw-font-normal tw-font-inter tw-leading-none">${switchOrganizationText}</span>
          `,
        prefixIcon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <g clip-path="url(#clip0_5977_9230)">
            <path d="M11.9006 7.71223C11.5142 7.71223 11.2005 8.03163 11.2005 8.42518V9.85109H2.38922L3.29444 8.92924C3.56817 8.65047 3.56817 8.19989 3.29444 7.92112C3.0207 7.64236 2.57825 7.64236 2.30451 7.92112L0.204251 10.06C0.139843 10.1256 0.0887361 10.2047 0.0530316 10.2917C-0.0176772 10.4664 -0.0176772 10.6624 0.0530316 10.8364C0.0887361 10.9234 0.139843 11.0025 0.204251 11.0681L2.30451 13.207C2.44103 13.346 2.62025 13.4159 2.79947 13.4159C2.9787 13.4159 3.15792 13.346 3.29444 13.207C3.56817 12.9282 3.56817 12.4776 3.29444 12.1988L2.38922 11.277H11.9006C12.2871 11.277 12.6007 10.9576 12.6007 10.564V8.42518C12.6007 8.03163 12.2871 7.71223 11.9006 7.71223Z" 
            class="tw-fill-blue-600 dark:tw-fill-blue-500" />
            <path d="M13.947 3.16216C13.9113 3.07518 13.8602 2.99604 13.7957 2.93045L11.6955 0.791593C11.4218 0.512828 10.9793 0.512828 10.7056 0.791593C10.4318 1.07036 10.4318 1.52094 10.7056 1.79971L11.6108 2.72156H2.09939C1.71294 2.72156 1.3993 3.04096 1.3993 3.43451V5.57337C1.3993 5.96692 1.71294 6.28632 2.09939 6.28632C2.48584 6.28632 2.79947 5.96692 2.79947 5.57337V4.14746H11.6108L10.7056 5.06931C10.4318 5.34808 10.4318 5.79866 10.7056 6.07743C10.8421 6.21645 11.0213 6.28632 11.2005 6.28632C11.3797 6.28632 11.559 6.21645 11.6955 6.07743L13.7957 3.93857C13.8602 3.87298 13.9113 3.79384 13.947 3.70686C14.0177 3.5329 14.0177 3.33612 13.947 3.16216Z" 
            class="tw-fill-blue-600 dark:tw-fill-blue-500" />
            </g>
            <defs>
            <clipPath id="clip0_5977_9230">
            <rect width="14" height="14" fill="white"/>
            </clipPath>
            </defs>
          </svg>`
      })
    }
    return options
  }
  public setUserDropdownOptions(): void {
    const goToProfileText = this._translate.instant('v2.header.GO_TO_PROFILE')
    const logoutText = this._translate.instant('v2.header.LOGOUT')

    this.userDropdownOptions = [
      {
        key: UserOptions.PROFILE,
        value: `<div class="tw-flex tw-flex-col tw-items-start">
          <span class="tw-font-semibold tw-text-sm tw-leading-4 tw-text-gray-900 dark:tw-text-white" title="${this._auth.userDisplayName}">${this._auth.trimUserName(
          this._auth.userDisplayName
        )}</span>
          <a class="tw-font-normal tw-text-sm tw-leading-4 tw-text-gray-500 dark:tw-text-gray-300">
            ${goToProfileText}
          </a>
        </div>`,
        prefixIcon: `<svg class="tw-w-3.5 tw-h-3.5 tw-fill-blue-600 dark:tw-fill-blue-500" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 6.63158C9.00734 6.63158 10.6346 5.14705 10.6346 3.31579C10.6346 1.48453 9.00734 0 7 0C4.99266 0 3.36538 1.48453 3.36538 3.31579C3.36538 5.14705 4.99266 6.63158 7 6.63158Z" />
  <path d="M8.21154 7.36842H5.78846C4.71779 7.36959 3.69134 7.75812 2.93425 8.44879C2.17717 9.13946 1.75128 10.0759 1.75 11.0526V13.2632C1.75 13.4586 1.8351 13.646 1.98657 13.7842C2.13804 13.9224 2.34348 14 2.55769 14H11.4423C11.6565 14 11.862 13.9224 12.0134 13.7842C12.1649 13.646 12.25 13.4586 12.25 13.2632V11.0526C12.2487 10.0759 11.8228 9.13946 11.0657 8.44879C10.3087 7.75812 9.28221 7.36959 8.21154 7.36842Z" fill="#3F83F8"/>
</svg>`
      },
      // Add Beta Features option for super admin users
      {
        key: UserOptions.BETA_FEATURES,
        value: `<span class="tw-text-purple-600 dark:tw-text-purple-400">Beta Features</span>`,
        prefixIcon: `<svg class="tw-w-3.5 tw-h-3.5 tw-stroke-purple-600 dark:tw-stroke-purple-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L13.09 8.26L19 6L14.74 10.91L21 12L14.74 13.09L19 18L13.09 15.74L12 22L10.91 15.74L5 18L9.26 13.09L3 12L9.26 10.91L5 6L10.91 8.26L12 2Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`
      },
      {
        key: UserOptions.LOGOUT,
        value: `<span class="tw-text-red-600 dark:tw-text-red-500">${logoutText}</span>`,
        textColorClass: 'tw-text-red-600 dark:tw-text-red-500',
        prefixIcon: `<svg class="tw-w-3.5 tw-h-3.5 tw-stroke-red-600 dark:tw-stroke-red-500 tw-fill-none" viewBox="0 0 16 16" > <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h11m0 0-4-4m4 4-4 4m-5 3H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h3" /> </svg>`
      }
    ]

    if (this._auth.user?.is_admin) {
      const adminToolsText = this._translate.instant('v2.header.ADMIN_TOOLS')

      this.userDropdownOptions.splice(1, 0, {
        key: UserOptions.ADMIN_TOOLS,
        value: `<span class="tw-text-gray-500 dark:tw-text-gray-300">${adminToolsText}</span>`,
        prefixIcon: `<svg class="tw-w-3.5 tw-h-3.5 tw-fill-blue-600 dark:tw-fill-blue-500" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 6.63158C9.00734 6.63158 10.6346 5.14705 10.6346 3.31579C10.6346 1.48453 9.00734 0 7 0C4.99266 0 3.36538 1.48453 3.36538 3.31579C3.36538 5.14705 4.99266 6.63158 7 6.63158Z" />
  <path d="M8.21154 7.36842H5.78846C4.71779 7.36959 3.69134 7.75812 2.93425 8.44879C2.17717 9.13946 1.75128 10.0759 1.75 11.0526V13.2632C1.75 13.4586 1.8351 13.646 1.98657 13.7842C2.13804 13.9224 2.34348 14 2.55769 14H11.4423C11.6565 14 11.862 13.9224 12.0134 13.7842C12.1649 13.646 12.25 13.4586 12.25 13.2632V11.0526C12.2487 10.0759 11.8228 9.13946 11.0657 8.44879C10.3087 7.75812 9.28221 7.36959 8.21154 7.36842Z" fill="#3F83F8"/>
</svg>`
      })
    }
    this.dropdownHeightInPX.set(this._menuDropdownHeightInPX(this.userDropdownOptions.length))
  }

  /**
   * Load parent organizations from the service
   */
  public loadParentOrganizations(): void {
    this._workspaceService
      .getParentOrganizations()
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe({
        next: (response: ParentOrganization[]) => {
          this.parentOrganizations.set(response || [])
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.message || 'An error occurred while loading parent organizations')
          this.parentOrganizations.set([])
        }
      })
  }

  /**
   * @method logout
   * @description Logs out the user from the application
   */
  public logout() {
    this._auth.signOut().pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }
  public onNavigateToCrewClicked(): void {
    this._auth.navigateToCrew()
  }

  /**
   * @description Handle actions on user option clicked from dropdown
   * @param event DropdownOption
   */
  public onUserOptionClicked(event: DropdownOption): void {
    switch (event.key) {
      case UserOptions.PROFILE:
        this._auth.navigateToPage('profile')
        break
      case UserOptions.ADMIN_TOOLS:
        this._auth.navigateToPage('admin-tools')
        break
      case UserOptions.BETA_FEATURES:
        this.openBetaFeatures()
        break
      case UserOptions.LOGOUT:
        this.logout()
        break
    }
  }

  /**
   * Opens the Beta Features modal for super admin users
   */
  public openBetaFeatures(): void {
    this.betaFeaturesModal.open()
  }
  /**
   * Handle workspace dropdown actions such as opening organization settings.
   * @param {DropdownOption} event Selected dropdown option
   */
  public onWorkspaceOptionClicked(event: DropdownOption): void {
    switch (event.key) {
      case WorkspaceOptions.SETTINGS:
        this._router.navigateByUrl(getOrganizationSettingsUrl())
        break
      case WorkspaceOptions.SWITCH_WORKSPACE:
        this.isOrganizationSwitcherOpen.set(true)
        break
      case WorkspaceOptions.ADD_NEW_WORKSPACE:
        this.loadParentOrganizations()
        this.toggleAddOrganizationModal()
        break
    }
  }

  /**
   * Initialize mobile view detection
   */
  private _initMobileViewDetection() {
    const mediaQuery = window.matchMedia('(max-width: 1023px)') // lg breakpoint in Tailwind is 1024px
    this.isMobileView.set(mediaQuery.matches)

    // Listen only when the breakpoint changes (not on every resize)
    const handler = (e: MediaQueryListEvent) => this.isMobileView.set(e.matches)
    mediaQuery.addEventListener('change', handler)
    this._destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', handler))
  }

  /**
   * Navigates to the home route based on role.
   * If wizard is open with unsaved changes, shows modal and prevents navigation until user acts.
   */
  public navigateHome(event?: MouseEvent) {
    if (!this._auth.isLoggedIn) return
    event?.preventDefault()
    event?.stopPropagation()
    const navigate = () => this._router.navigate(['workspaces', 'list'])
    this._wizardService.requestCloseAndThen(navigate)
  }

  public navigateToSettings() {
    this._router.navigate(['app-settings/profile'])
  }

  public toggleMobileDrawer(): void {
    this._mobileDrawerService.toggle()
  }
  public openUpgradeToProModal() {
    this._ModalService.toggleModal('invoice-plans-modal')
    this.isModalOpen.set(true)
  }

  public closeUpgradeToProModal() {
    this._ModalService.toggleModal('invoice-plans-modal')
    this.isModalOpen.set(false)
  }

  public toggleAddOrganizationModal() {
    this._ModalService.toggleModal('add-new-organization-modal')
  }

  public onOrganizationSubmit(formData: OrganizationFormData) {
    // close the modal
    this.toggleAddOrganizationModal()
    this._alertService.alert(Alert.SUCCESS, 'Organization created successfully!')
  }
  onOrganizationSelected(workspace: Workspace) {
    this._ModalService.toggleModal('organization-selection-modal')
    this.isOrganizationSwitcherOpen.set(false)
  }

  onOrganizationModalClosed() {
    // Only update the signal, don't toggle the modal again
    this.isOrganizationSwitcherOpen.set(false)
  }

  /**
   * Keeps the Tracked Jobs socket on the current workspace. Waits for the first role emission so an
   * Accountant never connects with the service's default role; Accountants are always disconnected.
   */
  private _syncQueuedJobsSocket(): void {
    const workspaceSlug = this.workspace()?.slug
    const isRoleResolved = this._workspaceRoleVersion() > 0
    if (workspaceSlug && isRoleResolved && this.showQueuedJobsIcon()) this._queuedJobsService.connectSocket(workspaceSlug)
    else this._queuedJobsService.disconnectSocket()
  }
}
